import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../providers/AuthContext";
import { saveAs } from "file-saver";
import { calculateAge, formatAMPM } from "./utils/utils";
import "./CSS/ProfileView.css";

/* ─────────────────────────── small helpers ─────────────────────────── */
const v = (val) =>
  val !== undefined && val !== null && val !== "" ? val : "—";

const InfoCard = ({ icon, title, children }) => (
  <div className="info-card" id={title.replaceAll(' ','_')}>
    <div className="info-card-header">
      <i className={icon}></i>
      <h5>{title}</h5>
    </div>
    {children}
  </div>
);

const InfoTable = ({ rows }) => (
  <table className="info-table">
    <tbody>
      {rows.filter(Boolean).map(([label, value], i) => (
        <tr key={i}>
          <td>{label}</td>
          <td>{value ?? "—"}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const QuickStat = ({ icon, label, value, dark }) => (
  <div className="quick-stat">
    <div
      className="quick-stat-icon"
      style={
        dark
          ? { background: "linear-gradient(135deg,#8b1a1a,#c0390a)" }
          : undefined
      }
    >
      <i className={icon}></i>
    </div>
    <div>
      <div className="quick-stat-label">{label}</div>
      <div className="quick-stat-value">{value}</div>
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  if (status === "active") return <span className="badge-active">Active</span>;
  if (status === "inactive")
    return <span className="badge-inactive">Inactive</span>;
  return <span className="badge-pending">Pending</span>;
};

/* ═══════════════════════════ MAIN COMPONENT ═══════════════════════════ */
export default function ProfileView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const profileRef = useRef();

  const [profile, setProfile] = useState(null);
  const [downloadData, setDownloadData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dwnloading, setDwnLoading] = useState(false);

  /* ── fetch profile ── */
  useEffect(() => {
    setLoading(true);
    fetch(`${process.env.REACT_APP_API_URL}profiles/${id}`, {
      credentials: "include",
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.success) setProfile(data.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching profile:", err);
        setLoading(false);
      });
  }, [id]);

  /* ── fetch download data ── */
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}profileDownloads/${id}`, {
      credentials: "include",
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.success) setDownloadData(data.data);
      })
      .catch((err) => console.error("Error fetching download data:", err));
  }, [id, profile]);

  /* ── handlers ── */
  const handleProfileDeletion = () => {
    if (!window.confirm("Delete this profile?")) return;
    fetch(`${process.env.REACT_APP_API_URL}profiles/${id}`, {
      method: "DELETE",
      credentials: "include",
    })
      .then((r) => r.json())
      .then((data) => {
        alert(data.message);
        navigate(process.env.PUBLIC_URL + "/");
      });
  };

  const handleProfileStatus = (status) => {
    if (!window.confirm("Do you want to change status?")) return;
    fetch(`${process.env.REACT_APP_API_URL}profiles/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ status }),
    })
      .then((r) => r.json())
      .then((data) => {
        alert(data.message);
        setProfile({ ...profile, status });
      });
  };

  const makeProfileLive = (profile_live) => {
    if (!window.confirm("Do you want to change status?")) return;
    fetch(`${process.env.REACT_APP_API_URL}profiles/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ profile_live }),
    })
      .then((r) => r.json())
      .then((data) => {
        alert(data.message);
        setProfile({ ...profile, profile_live });
        setDownloadData(null);
      });
  };

  const whatsAppShare = (url) => {
    const rawPdfUrl = `${process.env.REACT_APP_SERVER_DOMAIN}assets/uploads/pdf/${profile.name.replaceAll(" ", "_")}.pdf`;
    const encodedPdfUrl = encodeURI(url || rawPdfUrl);
    const message = `Hi, please find the PDF file here: ${encodedPdfUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank");
  };

  const handleProfileDownloads = async () => {
    try {
      setDwnLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}profileDownloads`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            formData: { profile_id: id, manager_id: user.id },
            profileData: profile,
          }),
        },
      );
      const result = await response.json();
      setProfile({ ...profile, profile_live: "inactive" });
      saveAs(result.file_url, profile.name + ".pdf");
      whatsAppShare(result.file_url);
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Something went wrong!");
    } finally {
      setDwnLoading(false);
    }
  };

  /* ── photo urls ── */
  const photoUrls =
    profile?.photos?.map(
      (p) => `${process.env.REACT_APP_SERVER_DOMAIN}${p.photo_path}`,
    ) ?? [];

  /* ── derived role flags (same logic as original) ── */
  const isOwnerManager =
    user?.role === "manager" && user?.id === profile?.manager?._id;
  const isAdmin = user?.role === "admin";

  /* ════════════════════════ LOADING / NOT FOUND ════════════════════════ */
  if (loading) {
    return (
      <>
        <div className="text-center py-5">
          <div
            className="spinner-border"
            style={{ color: "var(--crimson)" }}
            role="status"
          />
          <p
            className="mt-3"
            style={{ color: "var(--crimson)", fontWeight: 500 }}
          >
            Loading profile…
          </p>
        </div>
      </>
    );
  }

  if (!profile) {
    return (
      <>
        <div className="text-center py-5">
          <i
            className="fas fa-user-times"
            style={{ fontSize: "3rem", color: "var(--crimson)", opacity: 0.4 }}
          />
          <p className="mt-3 fw-bold">Profile not found.</p>
        </div>
      </>
    );
  }

  /* ════════════════════════════ RENDER ════════════════════════════════ */
  return (
    <>
      {/* Download spinner overlay */}
      {dwnloading && (
        <div className="overlay">
          <div className="text-center">
            <div className="spinner-ring mb-3" />
            <p style={{ color: "#fff", fontWeight: 600 }}>Generating PDF…</p>
          </div>
        </div>
      )}

      {/* ── Breadcrumb ── */}
      <div className="breadcrumb-bar">
        <div className="container">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to={process.env.PUBLIC_URL + "/"}>
                <i className="fas fa-home me-1" />
                Home
              </Link>
            </li>
            <li className="breadcrumb-item">
              <Link to={process.env.PUBLIC_URL + "/"}>Profiles</Link>
            </li>
            <li className="breadcrumb-item active">{profile.name}</li>
          </ol>
        </div>
      </div>


      {/* ── Profile Hero ── */}
      <section className="profile-hero">
        <div className="container">
          <div className="row align-items-center g-4">
            <div className="col-auto">
              <div className="profile-photo-wrap">
                {photoUrls.length > 0 ? (
                  <img
                    src={photoUrls[0]}
                    alt={profile.name}
                    className="profile-photo"
                  />
                ) : (
                  <div className="profile-photo-placeholder">
                    <img
                      src={`${process.env.PUBLIC_URL}/${profile.gender}.png`}
                      alt={profile.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "0px -10px",
                        borderRadius: 10,
                      }}
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.nextSibling.style.display = "flex";
                      }}
                    />
                    <i
                      className="fas fa-user-circle"
                      style={{
                        fontSize: "4.5rem",
                        color: "#c4a88a",
                        display: "none",
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="col">
              <div className="profile-hero-id">
                <i className="fas fa-id-badge me-1" />
                {v(profile.profile_id)}
              </div>
              <h1 className="profile-hero-name">{v(profile.name)}</h1>
              <div className="d-flex flex-wrap gap-2 mb-3">
                {profile.gender && (
                  <HeroTag icon="fas fa-venus-mars" label={profile.gender} />
                )}
                {profile.dob && (
                  <HeroTag
                    icon="fas fa-birthday-cake"
                    label={calculateAge(profile.dob)}
                  />
                )}
                {profile.height && (
                  <HeroTag
                    icon="fas fa-ruler-vertical"
                    label={profile.height}
                  />
                )}
                {profile.nativePlace && (
                  <HeroTag
                    icon="fas fa-map-marker-alt"
                    label={profile.nativePlace}
                  />
                )}
                {profile.jobRole && (
                  <HeroTag icon="fas fa-briefcase" label={profile.jobRole} />
                )}
              </div>
              <div className="d-flex flex-wrap gap-2">
                {profile.profile_live !== "inactive" && (
                <><button
                    className="btn-hero-primary"
                    onClick={handleProfileDownloads}
                    disabled={dwnloading}
                  >
                    <i className="fas fa-file-download" />
                    {dwnloading ? "Downloading…" : "Download as PDF"}
                  </button><button
                    className="btn-hero-outline"
                    onClick={() => whatsAppShare()}
                  >
                      <i className="fab fa-whatsapp" /> WhatsApp Share
                    </button></>
              )}
              {/* Centre: live / downloaded status */}
            <LiveStatus
              profile={profile}
              downloadData={downloadData}
              user={user}
              onMakeLive={makeProfileLive}
            />
                
              </div>
            </div>

            {/* Admin Actions */}
              {(isOwnerManager || isAdmin) && (
                <div className="admin-panel col-3">
                  <h6>
                    <i
                      className="fas fa-user-cog me-2"
                      style={{ color: "var(--gold)" }}
                    />
                    Admin Actions
                  </h6>
                  <Link
                    to={`${process.env.PUBLIC_URL}/editProfile/${id}`}
                    className="btn-sidebar btn-s-edit"
                  >
                    <i className="fas fa-edit" /> Edit Profile
                  </Link>
                  {isAdmin && (
                    <>
                      <SidebarStatusButton
                        status={profile.status}
                        onToggle={handleProfileStatus}
                      />
                      <button
                        className="btn-sidebar btn-s-delete"
                        onClick={handleProfileDeletion}
                      >
                        <i className="fas fa-trash-alt" /> Delete Profile
                      </button>
                    </>
                  )}
                </div>
              )}
          </div>
        </div>
      </section>

      {/* ── Main Content ── */}
      <section className="py-4" ref={profileRef}>
        <div className="container">
          <div className="row g-4">
            {/* LEFT: Detail Sections */}
            <div className="col-lg-8">
              <div
                className="info-card"
                style={{
                  borderRadius: 16,
                  overflow: "hidden",
                  marginBottom: 24,
                }}
              >
                <ul
                  className="nav nav-tabs-custom"
                  style={{ padding: "0 8px", background: "#fff" }}
                >
                  <li className="nav-item">
                    <a
                      className="nav-link active"
                      href="#Personal_Information"
                      onclick="return false"
                    >
                      Personal
                    </a>
                  </li>
                  <li className="nav-item">
                    <a className="nav-link" href="#Education_&_Career" onclick="return false">
                      Education
                    </a>
                  </li>
                  <li className="nav-item">
                    <a className="nav-link" href="#Family_Details" onclick="return false">
                      Family
                    </a>
                  </li>
                  <li className="nav-item">
                    <a className="nav-link" href="#Other_Details" onclick="return false">
                      Other Details
                    </a>
                  </li>
                </ul>
              </div>

              {/* Personal Information */}
              <InfoCard icon="fas fa-user" title="Personal Information">
                <InfoTable
                  rows={[
                    profile.manager && [
                      "Manager Name",
                      v(profile.manager.name),
                    ],
                    ["Profile Id", 
 <span style={{fontFamily: 'monospace', background: '#f5ece0', padding: '2px 8px', borderRadius: 4, fontWeight: 700}}>{v(profile.profile_id)}</span>

                    ],
                    [
                      "Full Name",
                      <strong key="name">{v(profile.name)}</strong>,
                    ],
                    ["Age", calculateAge(profile.dob)],
                    ["Gender", v(profile.gender)],
                    [
                      "Date of Birth",
                      profile.dob
                        ? new Date(profile.dob).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "long",
                            year: "numeric",
                          })
                        : "—",
                    ],
                    ["Time of Birth", formatAMPM(profile.tob)],
                    ["Place of Birth", v(profile.pob)],
                    [
                      "Nakshatram / Padam",
                      profile.nakshatram
                        ? `${profile.nakshatram} - ${v(profile.padam)}`
                        : null,
                    ],
                    ["Rasi", v(profile.rasi)],
                    ["Height", v(profile.height)],
                    ["Complexion", v(profile.complexion)],
                  ]}
                />
              </InfoCard>

              {/* Education & Career */}
              <InfoCard icon="fas fa-graduation-cap" title="Education & Career">
                <InfoTable
                  rows={[
                    ["Education", v(profile.education)],
                    ["College", v(profile.college)],
                    ["Job Type", v(profile.jobType)],
                    ["Job Role", v(profile.jobRole)],
                    ["Company", v(profile.company)],
                    [
                      "Salary (per annum)",
                      profile.salary ? `₹${profile.salary}LPA` : null,
                    ],
                    ["Work Place", v(profile.workPlace)],
                  ]}
                />
              </InfoCard>

              {/* Family Details */}
              <InfoCard icon="fas fa-users" title="Family Details">
                <InfoTable
                  rows={[
                    ["Father's Name", v(profile.fatherName)],
                    ["Father's Job", v(profile.fatherJob)],
                    ["Mother's Name", v(profile.motherName)],
                    ["Mother's Job", v(profile.motherJob)],
                    ...[1, 2, 3, 4]
                      .filter((n) => profile[`sibling${n}`])
                      .map((n) => [
                        `Sibling ${n}`,
                        `${profile[`sibling${n}`]} (${v(profile[`siblingJob${n}`])})`,
                      ]),
                  ]}
                />
              </InfoCard>

              {/* Other Details */}
              <InfoCard icon="fas fa-info-circle" title="Other Details">
                <InfoTable
                  rows={[
                    ["Caste", v(profile.caste)],
                    ["Sub-caste", v(profile.subCaste)],
                    ["Address", v(profile.address)],
                    ["Native Place", v(profile.nativePlace)],
                    ["Partner Preference", v(profile.partnerPreference)],
                    ["Contact", v(profile.contact)],
                  ]}
                />
              </InfoCard>
            </div>

            {/* RIGHT: Sidebar */}
            <div className="col-lg-4">
              {/* Photo gallery — carousel + lightbox */}
              {photoUrls.length > 0 && (
                <PhotoCarousel photos={photoUrls} />
              )}

              

              {/* Quick Overview */}
              <div className="sidebar-card">
                <h6>
                  <i className="fas fa-chart-bar me-2" />
                  Quick Overview
                </h6>
                <QuickStat
                  icon="fas fa-id-badge"
                  label="Profile ID"
                  value={v(profile.profile_id)}
                />
                <QuickStat
                  icon="fas fa-birthday-cake"
                  label="Age"
                  value={calculateAge(profile.dob)}
                />
                <QuickStat
                  icon="fas fa-ruler-vertical"
                  label="Height"
                  value={v(profile.height)}
                />
                <QuickStat
                  icon="fas fa-briefcase"
                  label="Job Role"
                  value={v(profile.jobRole)}
                />
                <QuickStat
                  icon="fas fa-building"
                  label="Company"
                  value={v(profile.company)}
                />
                <QuickStat
                  icon="fas fa-map-marker-alt"
                  label="Work Place"
                  value={v(profile.workPlace || profile.nativePlace)}
                />
                <div className="quick-stat">
                  <div className="quick-stat-icon">
                    <i className="fas fa-circle" />
                  </div>
                  <div>
                    <div className="quick-stat-label">Status</div>
                    <div className="quick-stat-value">
                      <StatusBadge status={profile.status} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Astrological Details */}
              <div
                className="sidebar-card"
                style={{
                  background: "linear-gradient(135deg,#fff8ee,#fff)",
                  border: "1px solid #f0d8b0",
                }}
              >
                <h6>
                  <i className="fas fa-moon me-2" />
                  Astrological Details
                </h6>
                <QuickStat
                  dark
                  icon="fas fa-star"
                  label="Rasi"
                  value={v(profile.rasi)}
                />
                <QuickStat
                  dark
                  icon="fas fa-sun"
                  label="Nakshatram"
                  value={
                    profile.nakshatram
                      ? `${profile.nakshatram} - ${v(profile.padam)}`
                      : "—"
                  }
                />
                <QuickStat
                  dark
                  icon="fas fa-clock"
                  label="Time of Birth"
                  value={formatAMPM(profile.tob)}
                />
              </div>

              {/* Contact */}
              <div className="sidebar-card">
                <h6>
                  <i className="fas fa-phone-alt me-2" />
                  Contact
                </h6>
                <p
                  style={{
                    fontSize: ".81rem",
                    color: "#888",
                    marginBottom: 12,
                  }}
                >
                  Visible to admins and managers only.
                </p>
                {profile.contact ? (
                  <p style={{ fontWeight: 600, color: "var(--crimson)" }}>
                    <i
                      className="fas fa-phone me-2"
                      style={{ color: "var(--saffron)" }}
                    />
                    {profile.contact}
                  </p>
                ) : (
                  <p style={{ color: "#aaa", fontSize: ".82rem" }}>
                    Not provided
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

/* ═══════════════════════ SUB-COMPONENTS ═══════════════════════════════ */

/* ═══════════════════════ PHOTO CAROUSEL + LIGHTBOX ═══════════════════ */

function PhotoCarousel({ photos }) {
  const [current, setCurrent] = useState(0);
  const [lightbox, setLightbox] = useState(false); // open lightbox
  const total = photos.length;

  const prev = (e) => { e?.stopPropagation(); setCurrent((c) => (c - 1 + total) % total); };
  const next = (e) => { e?.stopPropagation(); setCurrent((c) => (c + 1) % total); };

  // Close lightbox on Escape, navigate with arrow keys
  useEffect(() => {
    if (!lightbox) return;
    const handler = (e) => {
      if (e.key === "Escape")     setLightbox(false);
      if (e.key === "ArrowLeft")  setCurrent((c) => (c - 1 + total) % total);
      if (e.key === "ArrowRight") setCurrent((c) => (c + 1) % total);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightbox, total]);

  return (
    <>
      {/* ── Carousel card ── */}
      <InfoCard icon="fas fa-images" title={`Photos (${total})`}>
        <div className="carousel-wrap">

          {/* Main image */}
          <div className="carousel-stage" onClick={() => setLightbox(true)}>
            <img
              src={photos[current]}
              alt={`Profile pic ${current + 1}`}
              className="carousel-img"
            />
            <div className="carousel-zoom-hint">
              <i className="fas fa-search-plus" /> Click to enlarge
            </div>
          </div>

          {/* Prev / Next arrows (only if >1 photo) */}
          {total > 1 && (
            <>
              <button className="carousel-btn carousel-btn-prev" onClick={prev} aria-label="Previous">
                <i className="fas fa-chevron-left" />
              </button>
              <button className="carousel-btn carousel-btn-next" onClick={next} aria-label="Next">
                <i className="fas fa-chevron-right" />
              </button>
            </>
          )}

          {/* Dots */}
          {total > 1 && (
            <div className="carousel-dots">
              {photos.map((_, i) => (
                <button
                  key={i}
                  className={`carousel-dot${i === current ? " active" : ""}`}
                  onClick={() => setCurrent(i)}
                  aria-label={`Go to photo ${i + 1}`}
                />
              ))}
            </div>
          )}

          {/* Counter */}
          <div className="carousel-counter">{current + 1} / {total}</div>
        </div>

        {/* Thumbnails strip */}
        {total > 1 && (
          <div className="carousel-thumbs">
            {photos.map((url, i) => (
              <button
                key={i}
                className={`thumb-btn${i === current ? " active" : ""}`}
                onClick={() => setCurrent(i)}
                aria-label={`Thumbnail ${i + 1}`}
              >
                <img src={url} alt={`Thumb ${i + 1}`} />
              </button>
            ))}
          </div>
        )}
      </InfoCard>

      {/* ── Lightbox ── */}
      {lightbox && (
        <div className="lightbox-overlay" onClick={() => setLightbox(false)}>
          <div className="lightbox-inner" onClick={(e) => e.stopPropagation()}>

            {/* Close */}
            <button className="lb-close" onClick={() => setLightbox(false)} aria-label="Close">
              <i className="fas fa-times" />
            </button>

            {/* Prev */}
            {total > 1 && (
              <button className="lb-nav lb-prev" onClick={prev} aria-label="Previous">
                <i className="fas fa-chevron-left" />
              </button>
            )}

            {/* Image */}
            <img
              src={photos[current]}
              alt={`Profile pic ${current + 1}`}
              className="lb-img"
            />

            {/* Next */}
            {total > 1 && (
              <button className="lb-nav lb-next" onClick={next} aria-label="Next">
                <i className="fas fa-chevron-right" />
              </button>
            )}

            {/* Caption */}
            <div className="lb-caption">
              Photo {current + 1} of {total}
              {total > 1 && (
                <span className="lb-dots">
                  {photos.map((_, i) => (
                    <button
                      key={i}
                      className={`lb-dot${i === current ? " active" : ""}`}
                      onClick={() => setCurrent(i)}
                    />
                  ))}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function HeroTag({ icon, label }) {
  return (
    <span className="hero-tag">
      <i className={icon} /> {label}
    </span>
  );
}

function LiveStatus({ profile, downloadData, user, onMakeLive }) {
  if (downloadData && profile.profile_live === "inactive") {
    const byYou = downloadData.manager_id?._id === user?.id;
    const when = new Date(downloadData.createdAt).toLocaleString();
    return (
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <span className="btn-hero-outline status-downloaded">
          <i className="fa-solid fa-download"></i>Downloaded by <b>{byYou ? "You" : downloadData.manager_id?.name}</b>{" "}
          on {when}
        </span>
        {byYou && (
          <button
            className="btn-make-live"
            onClick={() => onMakeLive("active")}
          >
            <i className="fas fa-power-off" /> Make Profile Live
          </button>
        )}
      </div>
    );
  }
  return (
    <span className="btn-hero-outline status-live" style={{color: '#07ff07'}}>
      <i className="fas fa-circle" style={{ fontSize: ".5rem" }} />
      This profile is live to download…
    </span>
  );
}



function SidebarStatusButton({ status, onToggle }) {
  if (status === "active")
    return (
      <button
        className="btn-sidebar btn-s-status"
        onClick={() => onToggle("inactive")}
      >
        <i className="fas fa-ban" /> Deactivate Profile
      </button>
    );
  if (status === "inactive")
    return (
      <button
        className="btn-sidebar btn-s-accept"
        onClick={() => onToggle("active")}
      >
        <i className="fas fa-check-circle" /> Activate Profile
      </button>
    );
  return (
    <button
      className="btn-sidebar btn-s-accept"
      onClick={() => onToggle("active")}
    >
      <i className="fas fa-check-circle" /> Accept Profile
    </button>
  );
}
